import { Navigate, useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { usePermissions } from '../hooks/usePermissions'
import { ROLES, isSuperAdminOrOwner } from '@jira-clone/shared-types'

interface PermissionRouteProps {
  module: string | string[]
  action?: string
  // When set to 'ADMIN', MEMBER/VIEWER are blocked even if they otherwise
  // have module.action permission - for pages gated by role tier rather
  // than by module access, e.g. Leads by Assignee (team-wide visibility).
  // 'SUPERADMIN' restricts to SUPERADMIN/OWNER only (e.g. Settings).
  minRole?: 'ADMIN' | 'SUPERADMIN'
  // Departments (or roles) that never see this page, even with the permission.
  // Mirrors the same filter on the sidebar entry so the menu and the URL agree.
  excludeDepartments?: string[]
  children: React.ReactNode
  fallback?: React.ReactNode
}

export function PermissionRoute({ module, action = 'VIEW', minRole, excludeDepartments, children, fallback }: PermissionRouteProps) {
  const { isLoading, user } = useAuth()
  const { can } = usePermissions()

  if (isLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  const meetsMinRole =
    !minRole ||
    isSuperAdminOrOwner(user?.role) ||
    (minRole === 'ADMIN' && user?.role === ROLES.ADMIN)

  const hasAccess = Array.isArray(module)
    ? module.some((m) => can(m, action))
    : can(module, action)

  const userDept = String(user?.department ?? '').toLowerCase()
  const userRole = String(user?.role ?? '').toLowerCase()
  const isExcluded = (excludeDepartments ?? []).some((d) => {
    const key = d.toLowerCase()
    return key === userDept || key === userRole
  })

  if (!hasAccess || !meetsMinRole || isExcluded) {
    return fallback || <UnauthorizedPage />
  }

  return <>{children}</>
}

function UnauthorizedPage() {
  const navigate = useNavigate()
  const { user } = useAuth()

  return (
    <div className="flex h-screen w-screen items-center justify-center bg-background">
      <div className="max-w-md text-center space-y-4">
        <div className="h-16 w-16 rounded-full bg-destructive/10 text-destructive flex items-center justify-center mx-auto">
          <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-foreground">Access Denied</h1>
        <p className="text-muted-foreground">
          You don't have permission to access this module.
          {user?.role !== ROLES.SUPERADMIN && ' Contact your administrator to request access.'}
        </p>
        <div className="flex justify-center gap-3">
          <button
            onClick={() => navigate('/app')}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  )
}
